import { AxiosResponse } from 'axios'
import { infiniteQueryOptions } from '@tanstack/react-query'
import { PeoplePage } from '../generated/api/people-page'
import { createUrl, definedParams, isRefusedRequest } from './helpers'
import axiosAuth from './config'

export type PeopleSource =
  | { kind: 'post-likers'; id: string }
  | { kind: 'post-readers'; id: string }
  | { kind: 'comment-likers'; id: string }

const peoplePath = (source: PeopleSource): string => {
  switch (source.kind) {
    case 'post-likers':
      return `/posts/${source.id}/likers`
    case 'post-readers':
      return `/posts/${source.id}/readers`
    default:
      return `/comments/${source.id}/likers`
  }
}

export const getPeoplePage = (
  source: PeopleSource,
  cursor: string | undefined,
  signal?: AbortSignal
): Promise<AxiosResponse<{ data: PeoplePage }>> => {
  return axiosAuth.get<{ data: PeoplePage }>(
    createUrl(peoplePath(source), definedParams({ cursor })),
    { signal }
  )
}

export const peopleQueryKey = (source: PeopleSource) =>
  [{ scope: 'people', entity: source.kind, id: source.id }] as const

export const peopleQueries = {
  page: (source: PeopleSource) =>
    infiniteQueryOptions({
      queryKey: peopleQueryKey(source),
      initialPageParam: undefined as string | undefined,
      queryFn: async ({ pageParam, signal }) =>
        (await getPeoplePage(source, pageParam, signal)).data.data,
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      retry: (failureCount, error) =>
        !isRefusedRequest(error) && failureCount < 3,
      staleTime: 0,
    }),
}
